import { Injectable } from '@angular/core';
import { AuthorizationService } from './authorization.service';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { map, Observable, of, tap, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { User } from '../models/users';
import { jwtDecode } from 'jwt-decode';
import { environment } from '../environments/enviroment';


export interface AuthSelectableCompany {
  companyPlatformId: string;
  companyId: string;
  companyName: string;
  role: string | null;
}

export interface AuthDirectSessionResponse {
  access_token: string;
  refresh_token: string;
  token_type?: string;
  expires_in?: number;
  clientAgent?: {
    user?: User;
    authToken?: string;
  };
}

export interface AuthOtpPendingResponse {
  requiresOtp: true;
  access_token: string;
  expiresIn?: number;
}

export interface AuthCompanySelectionResponse {
  requiresCompanySelection: true;
  selectionToken: string;
  expiresIn?: number;
  companies: AuthSelectableCompany[];
}

export type AuthLoginResponse =
  | AuthDirectSessionResponse
  | AuthOtpPendingResponse
  | AuthCompanySelectionResponse;

export interface AuthMeResponse {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  document?: string;
  documentType?: string;
  phone?: string;
  status?: boolean;
  enableOTP?: boolean;
  context?: {
    userPlatformId?: string;
    companyPlatformId?: string;
    companyId?: string;
    companyName?: string;
    platformName?: string;
    role?: string | null;
    roleScope?: string | null;
  };
}

export interface TurnstileConfigResponse {
  platform: string;
  enabled: boolean;
}

export interface OperationalSessionRefreshResponse {
  authToken: string;
  deviceSessionId: string;
  expiresIn: number;
}

@Injectable({
  providedIn: 'root'
})
export class UserValidationService {
  private readonly OTP_SESSION_TOKEN_KEY = 'otpSessionToken';
  private readonly OTP_EXPIRES_AT_KEY = 'otpExpiresAt';
  private readonly OTP_ISSUED_AT_KEY = 'otpIssuedAt';
  private readonly OTP_REQUIRED_KEY = 'requiresOtp';
  private readonly ACCESS_TOKEN_KEY = 'token';
  private readonly REFRESH_TOKEN_KEY = 'refresh-token';
  private readonly COMPANY_SELECTION_TOKEN_KEY = 'company-selection-token';
  private readonly SELECTABLE_COMPANIES_KEY = 'selectable-companies';
  private readonly COMPANY_SELECTION_EXPIRES_AT_KEY = 'company-selection-expires-at';

  private validateUrl = `${environment.mindxbkUrl}UserValidate/`;

  constructor(
    private http: HttpClient,
    private authService: AuthorizationService) { }

  domain = environment.apiUrl;
  authUrl = this.domain + 'auth/login';
  authSelectCompanyUrl = this.domain + 'auth/select-company';
  authSwitchCompanyUrl = this.domain + 'auth/switch-company';
  authRefreshUrl = this.domain + 'auth/refresh';
  authLogoutUrl = this.domain + 'auth/logout';
  authMeUrl = this.domain + 'auth/me';
  // Derivado de apiUrl para usar el proxy en dev (evita CORS) y el dominio correcto
  // en preprod (intxgpre.app) / prod (intxg.app), igual que el resto de endpoints auth.
  authTurnstileConfigUrl = this.domain + 'auth/turnstile-config';

  getTurnstileConfig(platform: string): Observable<TurnstileConfigResponse> {
    return this.http.get<TurnstileConfigResponse>(this.authTurnstileConfigUrl, {
      params: { platform }
    });
  }

  // TEMPORARY HOTFIX: hardcoded URL requested for production contingency
  // while Turnstile service is unstable. Remove when normal env-based config is restored.
  private readonly temporaryTurnstileConfigUrl = 'https://intxgpre.app/api/auth/turnstile-config?platform=Mindx%20Live';

  getTemporaryTurnstileConfig(): Observable<TurnstileConfigResponse> {
    return this.http.get<TurnstileConfigResponse>(this.temporaryTurnstileConfigUrl);
  }

  login(
    email: string,
    password: string,
    device_id?: string,
    device_name?: string
  ): Observable<User> {
    return this.authService.getToken().pipe(
      switchMap((token: string) => {
        const headers = new HttpHeaders({
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        });

        const body = {
          email,
          password,
          device_id: device_id || 'unknown',
          device_name: device_name || 'unknown'
        };

        return this.http.post<{ user: User, authToken: string }>(`${this.validateUrl}validate`, body, { headers }).pipe(
          map((response) => {
            if (!response) {
              throw new Error('Empty response from server');
            }

            if (!response.user) {
              throw new Error('Invalid response: missing user data');
            }

            if (response.authToken) {
              localStorage.setItem('authToken', response.authToken);
            }

            return response.user;
          }),
          catchError(this.handleError)
        );
      }),
      catchError((error) => {
        console.error('Login process failed:', error);
        return throwError(() => error);
      })
    );
  }

  logout(token_user: string): Observable<void> {
    const token = localStorage.getItem('auth_req');
    if (!token) {
      console.error('No se encontro token en localStorage (auth_req)');
      return throwError(() => new Error('No auth token available'));
    }

    const headers = new HttpHeaders({
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      'Authorization-User': `Bearer ${token_user}`
    });

    return this.http
      .post<void>(`${this.validateUrl}logout`, {}, { headers })
      .pipe(
        catchError(this.handleError)
      );
  }

  refreshOperationalSession(): Observable<OperationalSessionRefreshResponse> {
    const authReq = localStorage.getItem('auth_req');
    const authToken = localStorage.getItem('authToken');

    if (!authReq || !authToken) {
      return throwError(() => new Error('Missing operational session credentials'));
    }

    const headers = new HttpHeaders({
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: `Bearer ${authReq}`,
      'Authorization-User': `Bearer ${authToken}`
    });

    return this.http.post<OperationalSessionRefreshResponse>(
      `${this.validateUrl}refresh`,
      {},
      { headers }
    ).pipe(
      map((response) => {
        if (!response?.authToken) {
          throw new Error('Operational session refresh returned no authToken');
        }

        // The technical token is managed independently by AuthorizationService.
        localStorage.setItem('authToken', response.authToken);
        return response;
      })
    );
  }

  agent_logout_supervisor(agent_id: string): Observable<void> {
    const authReq = localStorage.getItem('auth_req');

    if (!authReq) {
      console.error('No se encontro token en localStorage (auth_req)');
      return throwError(() => new Error('No auth_req token available'));
    }

    const headers = new HttpHeaders({
      Accept: 'application/json',
      'Content-Type': 'application/json',
      // TEMPORARY: Do not send the operational token until the authToken
      // expiration issue is resolved. Restore this header for final validation.
      // 'Authorization-User': `Bearer ${authToken}`,
      'Authorization': `Bearer ${authReq}`
    });

    return this.http
      .post<void>(`${this.validateUrl}supervisor/${agent_id}/logout`, {}, { headers })
      .pipe(
        catchError(this.handleError)
      );
  }

  clean_user_data(
    email: string,
    password: string,
  ): Observable<void> {
    return this.authService.getToken().pipe(
      switchMap((token: string) => {
        const headers = new HttpHeaders({
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        });
        const body = {
          email,
          password
        };

        return this.http.post<void>(`${this.validateUrl}clean_user_data`, body, { headers }).pipe(
          map(() => {
            localStorage.removeItem('authToken');
          }),
          catchError(this.handleError)
        );
      })
    );
  }

  resendVerification(email: string): Observable<void> {
    return this.authService.getToken().pipe(
      switchMap((token: string) => {
        const headers = new HttpHeaders({
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        });

        return this.http.post<void>(`${this.validateUrl}resend-verification`, { email }, { headers }).pipe(
          catchError(this.handleError)
        );
      })
    );
  }

  login2(
    email: string,
    password: string,
    platform: string,
    device_id?: string,
    device_name?: string,
    turnstileToken?: string
  ): Observable<AuthLoginResponse> {
    return this.http.post<any>(this.authUrl, { email, password, platform, device_id, device_name, turnstileToken },
      { headers: { 'Content-Type': 'application/json' } }).pipe(
        switchMap(response => {
          if (response?.requiresCompanySelection && response?.selectionToken) {
            this.storePendingCompanySelection(response.selectionToken, response?.companies, response?.expiresIn);
            return of(response as AuthCompanySelectionResponse);
          }

          if (response?.requiresOtp && response?.access_token) {
            this.storeOtpSession(response.access_token, response?.expiresIn);
            return of(response as AuthOtpPendingResponse);
          }

          this.clearOtpSession();
          this.clearPendingCompanySelection();
          return this.persistAuthenticatedSession$(response as AuthDirectSessionResponse);
        })
      );
  }

  selectCompany(companyPlatformId: string): Observable<AuthLoginResponse> {
    const selectionToken = this.getCompanySelectionToken();
    if (!selectionToken) {
      return throwError(() => new Error('Missing company selection token'));
    }

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${selectionToken}`
    });

    return this.http.post<AuthLoginResponse>(
      this.authSelectCompanyUrl,
      { companyPlatformId },
      { headers }
    ).pipe(
      switchMap((response) => {
        if ((response as AuthOtpPendingResponse)?.requiresOtp && (response as AuthOtpPendingResponse)?.access_token) {
          this.clearPendingCompanySelection();
          this.storeOtpSession((response as AuthOtpPendingResponse).access_token, (response as AuthOtpPendingResponse).expiresIn);
          return of(response);
        }

        this.clearOtpSession();
        this.clearPendingCompanySelection();
        return this.persistAuthenticatedSession$(response as AuthDirectSessionResponse);
      }),
      catchError(err => throwError(() => err))
    );
  }

  switchCompany(companyPlatformId: string, refreshToken?: string | null): Observable<AuthDirectSessionResponse> {
    const accessToken = this.getAccessToken();
    if (!accessToken) {
      return throwError(() => new Error('Missing access token'));
    }

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`
    });

    const resolvedRefreshToken = refreshToken ?? this.getRefreshToken();
    const body: Record<string, string> = {
      companyPlatformId: String(companyPlatformId || '').trim()
    };

    if (resolvedRefreshToken) {
      body['refreshToken'] = resolvedRefreshToken;
    }

    return this.http.post<AuthDirectSessionResponse>(
      this.authSwitchCompanyUrl,
      body,
      { headers }
    ).pipe(
      switchMap((response) => this.persistAuthenticatedSession$(response)),
      catchError(err => throwError(() => err))
    );
  }

  getOtpSessionToken(): string | null {
    return sessionStorage.getItem(this.OTP_SESSION_TOKEN_KEY);
  }

  getOtpExpiresAt(): number | null {
    const value = sessionStorage.getItem(this.OTP_EXPIRES_AT_KEY);
    return value ? Number(value) : null;
  }

  getOtpIssuedAt(): number | null {
    const value = sessionStorage.getItem(this.OTP_ISSUED_AT_KEY);
    return value ? Number(value) : null;
  }

  requiresOtp(): boolean {
    return sessionStorage.getItem(this.OTP_REQUIRED_KEY) === 'true';
  }

  hasPendingOtpSession(): boolean {
    return !!this.getOtpSessionToken() && this.requiresOtp();
  }

  getAccessToken(): string | null {
    return localStorage.getItem(this.ACCESS_TOKEN_KEY);
  }

  getRefreshToken(): string | null {
    return localStorage.getItem(this.REFRESH_TOKEN_KEY);
  }

  getCompanySelectionToken(): string | null {
    return sessionStorage.getItem(this.COMPANY_SELECTION_TOKEN_KEY);
  }

  getSelectableCompanies(): AuthSelectableCompany[] {
    const raw = sessionStorage.getItem(this.SELECTABLE_COMPANIES_KEY);
    if (!raw) {
      return [];
    }

    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed as AuthSelectableCompany[] : [];
    } catch {
      return [];
    }
  }

  hasPendingCompanySelection(): boolean {
    return !!this.getCompanySelectionToken() && this.getSelectableCompanies().length > 0;
  }

  clearPendingCompanySelection(): void {
    sessionStorage.removeItem(this.COMPANY_SELECTION_TOKEN_KEY);
    sessionStorage.removeItem(this.SELECTABLE_COMPANIES_KEY);
    sessionStorage.removeItem(this.COMPANY_SELECTION_EXPIRES_AT_KEY);
  }

  clearOtpSession(): void {
    sessionStorage.removeItem(this.OTP_SESSION_TOKEN_KEY);
    sessionStorage.removeItem(this.OTP_EXPIRES_AT_KEY);
    sessionStorage.removeItem(this.OTP_ISSUED_AT_KEY);
    sessionStorage.removeItem(this.OTP_REQUIRED_KEY);
    sessionStorage.removeItem('loginToken');
    localStorage.removeItem('token-otp');
    localStorage.setItem('authy', 'false');
  }

  private setToken(token: string): void {
    const decodedToken: any = jwtDecode(token);
    if (!decodedToken.sub.hashedCode || decodedToken.sub.authy == false) {
      localStorage.setItem('authy', 'false');
    } else if (decodedToken.sub.hashedCode) {
      localStorage.setItem('authy', 'true');
    }
    localStorage.setItem(this.ACCESS_TOKEN_KEY, token);
  }

  private setRefreshToken(refreshToken: string): void {
    localStorage.setItem(this.REFRESH_TOKEN_KEY, refreshToken);
  }

  private clearAuthenticatedSession(): void {
    localStorage.removeItem(this.ACCESS_TOKEN_KEY);
    localStorage.removeItem(this.REFRESH_TOKEN_KEY);
    localStorage.removeItem('token-otp');
    localStorage.removeItem('authy');
  }

  getAuthy(): string | null {
    return localStorage.getItem('authy');
  }

  parseBoolean(value: string | null): boolean {
    return value?.toLowerCase() === 'true';
  }

  otpAuth(tempToken: string, otp: string): Observable<any> {
    if (!tempToken) {
      return throwError(() => new Error('Missing OTP session token'));
    }

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${tempToken}`
    });

    return this.http.post<any>(`${this.domain}auth/otp-sign-in`, { otp }, { headers }).pipe(
      switchMap(response => {
        this.clearOtpSession();
        this.clearPendingCompanySelection();
        return this.persistAuthenticatedSession$(response as AuthDirectSessionResponse);
      }),
      catchError(err => throwError(() => err))
    );
  }

  resendOtp(): Observable<any> {
    const otpSessionToken = this.getOtpSessionToken();

    if (!otpSessionToken) {
      return throwError(() => new Error('Missing OTP session token'));
    }

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${otpSessionToken}`
    });

    return this.http.post<any>(`${this.domain}auth/resend-otp`, {}, { headers }).pipe(
      tap(response => {
        if (response?.access_token) {
          this.storeOtpSession(response.access_token, response?.expiresIn);
        }
      }),
      map(response => response),
      catchError(err => throwError(() => err))
    );
  }

  private storeOtpSession(token: string, expiresIn?: number): void {
    const now = Date.now();
    const expiresAt = now + ((expiresIn ?? 300) * 1000);

    sessionStorage.setItem(this.OTP_SESSION_TOKEN_KEY, token);
    sessionStorage.setItem(this.OTP_EXPIRES_AT_KEY, String(expiresAt));
    sessionStorage.setItem(this.OTP_ISSUED_AT_KEY, String(now));
    sessionStorage.setItem(this.OTP_REQUIRED_KEY, 'true');
    localStorage.setItem('token-otp', token);
    localStorage.setItem('authy', 'true');
  }

  private storePendingCompanySelection(
    selectionToken: string,
    companies: AuthSelectableCompany[] = [],
    expiresIn?: number
  ): void {
    const expiresAt = Date.now() + ((expiresIn ?? 300) * 1000);
    sessionStorage.setItem(this.COMPANY_SELECTION_TOKEN_KEY, selectionToken);
    sessionStorage.setItem(this.SELECTABLE_COMPANIES_KEY, JSON.stringify(companies || []));
    sessionStorage.setItem(this.COMPANY_SELECTION_EXPIRES_AT_KEY, String(expiresAt));
  }

  private persistAuthenticatedSession$(response: AuthDirectSessionResponse): Observable<AuthDirectSessionResponse> {
    if (response?.access_token) {
      this.setToken(response.access_token);
    }

    if (response?.refresh_token) {
      this.setRefreshToken(response.refresh_token);
    }

    this.clearOtpSession();
    this.clearPendingCompanySelection();

    if (response?.clientAgent?.authToken) {
      localStorage.setItem('authToken', response.clientAgent.authToken);
    }

    return this.authService.getToken().pipe(
      map(() => response)
    );
  }

  getMe(): Observable<AuthMeResponse> {
    const accessToken = this.getAccessToken();
    if (!accessToken) {
      return throwError(() => new Error('Missing access token'));
    }

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`
    });

    return this.http.get<AuthMeResponse>(this.authMeUrl, { headers }).pipe(
      catchError(err => throwError(() => err))
    );
  }

  loadAuthenticatedUserProfile(): Observable<User> {
    return this.getMe().pipe(
      map((profile) => this.mapAuthProfileToUser(profile))
    );
  }

  refreshAuthenticatedSession(): Observable<AuthDirectSessionResponse> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      return throwError(() => new Error('Missing refresh token'));
    }

    return this.http.post<AuthDirectSessionResponse>(
      this.authRefreshUrl,
      { refreshToken },
      { headers: { 'Content-Type': 'application/json' } }
    ).pipe(
      switchMap((response) => this.persistAuthenticatedSession$(response)),
      catchError((error) => {
        this.clearAuthenticatedSession();
        return throwError(() => error);
      })
    );
  }

  logoutCurrentSession(allSessions = false): Observable<void> {
    const refreshToken = this.getRefreshToken();
    const request$ = refreshToken
      ? this.http.post<void>(
          this.authLogoutUrl,
          { refreshToken, allSessions },
          { headers: { 'Content-Type': 'application/json' } }
        )
      : of(void 0);

    return request$.pipe(
      tap(() => {
        this.clearAuthenticatedSession();
        this.clearOtpSession();
        this.clearPendingCompanySelection();
      }),
      catchError((error) => {
        this.clearAuthenticatedSession();
        this.clearOtpSession();
        this.clearPendingCompanySelection();
        return throwError(() => error);
      })
    );
  }

  hasAuthenticatedSession(): boolean {
    return this.hasValidAccessToken() || !!this.getRefreshToken();
  }

  hasValidAccessToken(): boolean {
    const accessToken = this.getAccessToken();
    return !!accessToken && !this.isTokenExpired(accessToken);
  }

  shouldRefreshAuthenticatedSession(bufferMs = 60000): boolean {
    const accessToken = this.getAccessToken();
    if (!accessToken) {
      return !!this.getRefreshToken();
    }

    return this.getTokenRemainingMs(accessToken) <= bufferMs;
  }

  shouldRefreshOperationalSession(bufferMs: number): boolean {
    const authToken = localStorage.getItem('authToken');
    if (!authToken) {
      return false;
    }

    const remainingMs = this.getTokenRemainingMs(authToken);
    if (remainingMs <= 0) {
      return true;
    }

    const effectiveBufferMs = this.getOperationalRefreshBufferMs(authToken, bufferMs);
    return remainingMs <= effectiveBufferMs;
  }

  ensureAuthenticatedSession(): Observable<boolean> {
    if (this.hasValidAccessToken()) {
      return of(true);
    }

    if (this.getRefreshToken()) {
      return this.refreshAuthenticatedSession().pipe(
        map(() => true),
        catchError(() => {
          this.clearAuthenticatedSession();
          return of(false);
        })
      );
    }

    this.clearAuthenticatedSession();
    return of(false);
  }

  getAuthenticatedUserId(): string {
    const payload = this.getAuthenticatedTokenPayload();
    return String(payload?.sub?.id || '').trim();
  }

  getActiveCompanyPlatformId(): string {
    const payload = this.getAuthenticatedTokenPayload();
    return String(payload?.sub?.companyPlatformId || '').trim();
  }

  getAuthenticatedPlatformName(): string {
    const payload = this.getAuthenticatedTokenPayload();
    return String(payload?.sub?.platform || environment.platformNameOrchGT || '').trim();
  }

  private getAuthenticatedTokenPayload(): any {
    const token = this.getAccessToken();
    if (!token) {
      return null;
    }

    try {
      return jwtDecode(token);
    } catch {
      return null;
    }
  }

  private isTokenExpired(token: string): boolean {
    return this.getTokenRemainingMs(token) <= 0;
  }

  private getTokenRemainingMs(token: string): number {
    try {
      const payload: any = jwtDecode(token);
      const exp = Number(payload?.exp || 0);
      if (!exp) {
        return 0;
      }

      return (exp * 1000) - Date.now();
    } catch {
      return 0;
    }
  }

  private getOperationalRefreshBufferMs(token: string, configuredBufferMs: number): number {
    try {
      const payload: any = jwtDecode(token);
      const issuedAt = Number(payload?.iat || 0) * 1000;
      const expiresAt = Number(payload?.exp || 0) * 1000;
      const lifetimeMs = expiresAt - issuedAt;

      // Short-lived test tokens refresh halfway through their lifetime instead of continuously.
      return lifetimeMs > 0 ? Math.min(configuredBufferMs, Math.floor(lifetimeMs / 2)) : configuredBufferMs;
    } catch {
      return configuredBufferMs;
    }
  }

  private mapAuthProfileToUser(profile: AuthMeResponse): User {
    const firstName = String(profile.firstName || '').trim();
    const lastName = String(profile.lastName || '').trim();
    const username = `${firstName} ${lastName}`.trim() || profile.email;

    return {
      id: String(profile.id),
      email: String(profile.email),
      username,
      version: '1.0',
      ip: '',
      last_update: new Date(),
      created_at: new Date(),
      status: profile.status === false ? 'Inactivo' : 'No Disponible',
      role: this.mapRole(profile.context?.role),
      password: ''
    };
  }

  private mapRole(roleName?: string | null): 'agent' | 'supervisor' | 'admin' {
    const normalized = String(roleName || '').toLowerCase();

    if (normalized.includes('supervisor')) {
      return 'supervisor';
    }

    if (normalized.includes('admin') || normalized.includes('owner') || normalized.includes('superadmin')) {
      return 'admin';
    }

    return 'agent';
  }

  private handleError(error: HttpErrorResponse) {
    console.error('UserValidationService error:', {
      status: error.status,
      statusText: error.statusText,
      message: error.message,
      error: error.error
    });

    if (error.status === 403) {
      if (error.error && typeof error.error === 'object') {
        if (error.error.message?.includes('not verified') ||
          error.error.reason === 'unverified') {
          error.error.reason = 'unverified';
        }
      }
    }

    if (error.status === 0) {
      console.error('Network error: Unable to connect to server');
    } else if (error.status >= 500) {
      console.error('Server error: Internal server error');
    }

    return throwError(() => error);
  }
}
